import React, { useMemo, useState } from "react";
import { List, Sparkles, CornerUpRight, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { buildHighlightOverview, availableCategories } from "@/components/reader/readingEngine";
import { HL_SWATCH } from "@/components/reader/HighlightControls";

const clip = (s, n = 160) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "");

// Compact Smart Highlights overview: every AI-highlighted passage in one list,
// grouped by category with counts + filter. Click jumps to the exact spot in the
// reader; optional "Explain" opens the Click-to-Explain card. NOT a new module —
// a fast in-reader revision + navigation aid. A term that belongs to several
// categories appears ONCE with small category labels.
export const HighlightsOverview = ({ model, data, layers, ratio = 1, onJump, onExplain, className }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");

    const catLabel = (catId) =>
        catId === "domain"
            ? (t(`rset.domainLabel.${data?.domain}`) !== `rset.domainLabel.${data?.domain}`
                ? t(`rset.domainLabel.${data?.domain}`)
                : t("rset.hlCat.domain.label"))
            : t(`rset.hlCat.${catId}.label`);

    const entries = useMemo(
        () => (data && layers ? buildHighlightOverview(model, data, layers) : []),
        [model, data, layers]
    );
    const cats = useMemo(
        () => availableCategories(data).filter((c) => layers?.[c.id]),
        [data, layers]
    );
    const counts = useMemo(() => {
        const m = {};
        for (const e of entries) for (const c of e.cats) m[c] = (m[c] || 0) + 1;
        return m;
    }, [entries]);

    const total = model?.totalTokens || 1;
    const shown = filter === "all" ? entries : entries.filter((e) => e.cats.includes(filter));

    const jump = (e) => { onJump?.(e.tokenStart); setOpen(false); };
    const explain = (e, ev) => {
        if (!onExplain) return;
        onExplain(e.text, e.context, ev.currentTarget.getBoundingClientRect(), Math.round(e.tokenStart * ratio));
        setOpen(false);
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    data-testid="highlights-overview-button"
                    variant="secondary"
                    size="sm"
                    className={cn("gap-1.5 bg-surface-3/60", className)}
                >
                    <List className="h-4 w-4" />
                    <span className="hidden sm:inline">{t("overview.button")}</span>
                    {entries.length > 0 && (
                        <span className="ml-0.5 rounded-full bg-[hsl(var(--primary))]/20 px-1.5 text-[10px] font-medium">{entries.length}</span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent
                side="right"
                className="flex w-full flex-col border-border bg-surface-1 p-0 sm:max-w-md"
                data-testid="highlights-overview-sheet"
            >
                <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                    <Sparkles className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                    <div className="min-w-0">
                        <SheetTitle className="font-sans-display text-sm font-semibold text-t1">{t("overview.title")}</SheetTitle>
                        <SheetDescription className="truncate text-xs text-t4">{t("overview.subtitle")}</SheetDescription>
                    </div>
                </div>

                {/* Category filter chips with counts */}
                <div className="flex flex-wrap gap-1.5 border-b border-border px-4 py-3">
                    <button
                        data-testid="overview-filter-all"
                        onClick={() => setFilter("all")}
                        className={cn(
                            "rounded-full border px-2.5 py-1 text-xs transition-colors",
                            filter === "all" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t3 hover:text-t1"
                        )}
                    >
                        {t("overview.all")} <span className="ml-0.5 font-mono-ui text-t4">{entries.length}</span>
                    </button>
                    {cats.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`overview-filter-${c.id}`}
                            onClick={() => setFilter(c.id)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                                filter === c.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t3 hover:text-t1"
                            )}
                        >
                            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: HL_SWATCH[c.id] }} />
                            {catLabel(c.id)} <span className="font-mono-ui text-t4">{counts[c.id] || 0}</span>
                        </button>
                    ))}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3" data-testid="overview-list">
                    {shown.length === 0 ? (
                        <p className="px-2 py-12 text-center text-sm text-t4">
                            {entries.length === 0 ? t("overview.empty") : t("overview.noMatch")}
                        </p>
                    ) : (
                        <div className="space-y-1.5">
                            {shown.map((e, i) => (
                                <div
                                    key={`${e.tokenStart}-${i}`}
                                    data-testid="overview-item"
                                    className="rounded-xl border border-border bg-surface-2/50 p-3 transition-colors hover:border-[hsl(var(--primary))]/40"
                                >
                                    <button
                                        data-testid="overview-item-jump"
                                        onClick={() => jump(e)}
                                        className="block w-full text-left"
                                    >
                                        <span className={cn("rg-hl", `rg-hl-${e.primary}`, "font-reading text-sm text-t1")}>{e.text}</span>
                                        {e.count > 1 && <span className="ml-1.5 font-mono-ui text-[10px] text-t4">{e.count}×</span>}
                                    </button>
                                    {e.context && <p className="mt-1.5 text-xs italic leading-relaxed text-t4">{clip(e.context)}</p>}
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                        {e.cats.map((c) => (
                                            <span key={c} className="inline-flex items-center gap-1 rounded-full bg-surface-3/60 px-1.5 py-0.5 text-[10px] text-t3">
                                                <span className="h-2 w-2 rounded-[2px]" style={{ backgroundColor: HL_SWATCH[c] }} />
                                                {catLabel(c)}
                                            </span>
                                        ))}
                                        <span className="ml-auto flex items-center gap-1">
                                            <span className="mr-1 font-mono-ui text-[10px] text-t4">{t("overview.position", { n: Math.round((e.tokenStart / total) * 100) })}</span>
                                            <button data-testid="overview-jump-btn" onClick={() => jump(e)} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-t3 transition-colors hover:bg-surface-3 hover:text-t1">
                                                <CornerUpRight className="h-3 w-3" /> {t("overview.jump")}
                                            </button>
                                            {onExplain && (
                                                <button data-testid="overview-explain-btn" onClick={(ev) => explain(e, ev)} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-[hsl(var(--primary))] transition-colors hover:bg-surface-3">
                                                    <MessageCircleQuestion className="h-3 w-3" /> {t("overview.explain")}
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
